import { useEffect } from "react";
import { anchorIdOf, relPos, VIEWPORT_ANCHOR } from "./anchor.js";

const MOVE_MS = 50;

export function usePointerBroadcast(
  enabled: boolean,
  send: (p: { anchor: string | null; x: number; y: number }) => void,
) {
  useEffect(() => {
    if (!enabled) return;
    let last = 0;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const handle = (target: Element | null, clientX: number, clientY: number) => {
      last = Date.now();
      const id = anchorIdOf(target);
      const el = id ? target?.closest(id.startsWith("btn:") ? "button" : "[data-anchor]") : null;
      if (id && el) send({ anchor: id, ...relPos(el.getBoundingClientRect(), clientX, clientY) });
      else send({ anchor: VIEWPORT_ANCHOR, x: clientX / innerWidth, y: clientY / innerHeight });
    };
    const move = (e: PointerEvent) => {
      const target = e.target instanceof Element ? e.target : null;
      const { clientX, clientY } = e;
      if (timer) clearTimeout(timer);
      timer = null;
      const wait = MOVE_MS - (Date.now() - last);
      if (wait <= 0) return handle(target, clientX, clientY);
      timer = setTimeout(() => {
        timer = null;
        handle(target, clientX, clientY);
      }, wait);
    };
    const out = (e: MouseEvent) => {
      if (timer) clearTimeout(timer);
      timer = null;
      if (!e.relatedTarget) send({ anchor: null, x: 0, y: 0 });
    };
    window.addEventListener("pointermove", move, { passive: true });
    document.addEventListener("mouseout", out);
    return () => {
      if (timer) clearTimeout(timer);
      window.removeEventListener("pointermove", move);
      document.removeEventListener("mouseout", out);
    };
  }, [enabled, send]);
}
